import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { User, Users, Shield, Search, AlertCircle, Variable, Globe } from 'lucide-react';
import { Button } from './Button';
import { FieldGroup, Input, Select, Label, Textarea } from './Input';
import { Pill } from './Pill';
import { getUsers, getGroups, getRoleCatalog } from '../../services/api';
// RC-221 — read legal-value Sets from the shared validator so the picker's
// dropdowns can never drift from what v3Validate/v4Validate/ConfigSchemaValidator
// accept. If someone adds a new DynamicResolveVia or ExternalNotifyVia, the
// picker picks it up automatically.
import {
  LEGAL_DYNAMIC_RESOLVE_VIA,
  LEGAL_EXTERNAL_NOTIFY_VIA,
} from '../../pages/shared/formTaskAssignmentValidator';

/**
 * AssigneePicker — V2 polymorphic assignee picker for RC-157f.
 *
 * <p>Toggleable USER / GROUP / ROLE selector that returns a structured
 * <code>{ assigneeType, assigneeRef }</code> shape matching the BE
 * <code>AssigneeResolverService.AssigneeRef</code> contract:</p>
 *
 * <pre>
 *   { assigneeType: 'USER',  assigneeRef: { userId: '&lt;uuid&gt;' } }
 *   { assigneeType: 'GROUP', assigneeRef: { groupId: '&lt;uuid&gt;' } }
 *   { assigneeType: 'ROLE',  assigneeRef: { roleName: 'Compliance Officer' } }
 * </pre>
 *
 * <p><b>Single-tenant scope (FORM_INGESTION_MODEL.md decision #4).</b>
 * Caller passes <code>tenantId</code> as the resolution scope; this
 * component never crosses tenant boundaries when fetching pickable
 * candidates. Users / groups / roles are filtered to that tenant.</p>
 *
 * <p><b>Composition contract.</b> Built entirely on V2 primitives
 * (Button, FieldGroup, Input, Select, Label, Pill). NO inline
 * <code>style={{}}</code> with hardcoded padding/colors/borders, per
 * <code>feedback_v2_primitives_no_inline_style</code>. Layout-only
 * Tailwind utilities (flex/grid/gap/mt) are allowed.</p>
 *
 * <p><b>Loading + error handling.</b> Each picker body fetches lazily
 * on tab activation (so a USER-only consumer doesn't hit /groups). Loading
 * and error states render inline with V2 Pill + helper text, never with
 * a blocking spinner overlay.</p>
 *
 * @param {Object}   props
 * @param {string}   props.tenantId          Active-tenant UUID (REQUIRED).
 * @param {Object}   [props.value]           Current selection: { assigneeType, assigneeRef }.
 * @param {Function} props.onChange          Called with { assigneeType, assigneeRef }.
 * @param {boolean}  [props.readOnly=false]  Disable all controls.
 * @param {boolean}  [props.required=false]  Render the required-asterisk on the label.
 * @param {string}   [props.label='Assignee']
 * @param {string}   [props.hint]            Helper text under the picker.
 * @param {string}   [props.error]           Inline error (e.g. "no users in tenant").
 */
export function AssigneePicker({
  tenantId,
  value,
  onChange,
  readOnly = false,
  required = false,
  label = 'Assignee',
  hint,
  error,
}) {
  // Local mirror of the assignee-type tab. Initialised from value.assigneeType
  // so reopening with an existing GROUP assignment lands on the GROUP tab.
  const initialType = value?.assigneeType || 'USER';
  const [assigneeType, setAssigneeType] = useState(initialType);

  // Per-type candidate caches. Keyed by tenantId so a tenant switch invalidates.
  const [users, setUsers] = useState(null);     // null = not loaded
  const [groups, setGroups] = useState(null);
  const [roles, setRoles] = useState(null);

  // Per-type fetch state.
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Search box — type-ahead client-side filter over the loaded candidate set.
  // No server-side search endpoint exists yet (RC-157a discovery), so we
  // filter the list in-memory. Adequate for tenant-scoped lists which are
  // typically O(tens to low hundreds), not thousands.
  const [search, setSearch] = useState('');

  const handleTypeChange = useCallback((nextType) => {
    if (readOnly) return;
    if (nextType === assigneeType) return;
    setAssigneeType(nextType);
    setSearch('');
    // Switching tabs CLEARS the selection — preserving a USER selection
    // when flipping to GROUP would be misleading. The operator must
    // re-pick on the new tab.
    //
    // RC-221: DYNAMIC + EXTERNAL_SUBJECT seed with the canonical default
    // ref shape so downstream form validators see a fully-formed (if
    // incomplete) block rather than a bare {} — keeps the "configure me"
    // hint in the right state instead of the picker showing an
    // undifferentiated empty box.
    const seededRef = defaultRefFor(nextType);
    onChange?.({ assigneeType: nextType, assigneeRef: seededRef });
  }, [assigneeType, readOnly, onChange]);

  // RC-221 — per-mode patch helpers for the DYNAMIC + EXTERNAL_SUBJECT
  // sub-forms. Each merges the field-level change into the existing
  // assigneeRef shape and re-emits the full {assigneeType, assigneeRef}.
  const patchDynamicRef = useCallback((patch) => {
    if (readOnly) return;
    const next = { ...(value?.assigneeRef || defaultRefFor('DYNAMIC')), ...patch };
    onChange?.({ assigneeType: 'DYNAMIC', assigneeRef: next });
  }, [readOnly, value, onChange]);

  const patchExternalRef = useCallback((patch) => {
    if (readOnly) return;
    const next = { ...(value?.assigneeRef || defaultRefFor('EXTERNAL_SUBJECT')), ...patch };
    onChange?.({ assigneeType: 'EXTERNAL_SUBJECT', assigneeRef: next });
  }, [readOnly, value, onChange]);

  // Lazy fetch per tab.
  useEffect(() => {
    // RC-221 — DYNAMIC + EXTERNAL_SUBJECT render config sub-forms, NOT
    // pickable candidate lists. No fetch needed; skip to avoid emitting
    // an irrelevant "loading users…" message under those tabs.
    if (assigneeType === 'DYNAMIC' || assigneeType === 'EXTERNAL_SUBJECT') {
      return undefined;
    }
    let cancelled = false;
    async function load() {
      if (!tenantId) return;
      setFetchError(null);
      try {
        if (assigneeType === 'USER' && users == null) {
          setLoading(true);
          const res = await getUsers({ tenantId });
          if (cancelled) return;
          const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
          setUsers(list);
        } else if (assigneeType === 'GROUP' && groups == null) {
          setLoading(true);
          const res = await getGroups({ tenantId });
          if (cancelled) return;
          const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
          setGroups(list);
        } else if (assigneeType === 'ROLE' && roles == null) {
          setLoading(true);
          const res = await getRoleCatalog();
          if (cancelled) return;
          // RC-FormDesigner-AssignmentUX (#213, 2026-06-22) — BE endpoint
          // GET /api/v1/rbac/roles returns {permsRev, roles:[...]} per
          // RbacController.roles(). Prior unwrap only checked res.data and
          // bare-array, falling through to [] which surfaced as the
          // misleading "No roles available" empty state. Defensive
          // multi-shape unwrap covers res.roles (current BE), res.data
          // (envelope convention), and bare array (future API change).
          const list = Array.isArray(res?.roles) ? res.roles
                     : Array.isArray(res?.data)  ? res.data
                     : Array.isArray(res)        ? res
                     : [];
          setRoles(list);
        }
      } catch (e) {
        if (!cancelled) setFetchError(e?.message || 'Could not load candidates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [tenantId, assigneeType, users, groups, roles]);

  // ── Filtered candidates per the search box ────────────────────────────
  const filteredUsers = useMemo(() => {
    if (!users) return [];
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u?.name || '').toLowerCase().includes(q) ||
      (u?.email || '').toLowerCase().includes(q),
    );
  }, [users, search]);

  const filteredGroups = useMemo(() => {
    if (!groups) return [];
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => (g?.name || '').toLowerCase().includes(q));
  }, [groups, search]);

  const filteredRoles = useMemo(() => {
    if (!roles) return [];
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter((r) => (r?.name || r?.role || '').toLowerCase().includes(q));
  }, [roles, search]);

  // ── Selection callbacks ────────────────────────────────────────────────
  const selectUser  = (userId) => onChange?.({ assigneeType: 'USER',  assigneeRef: { userId } });
  const selectGroup = (groupId) => onChange?.({ assigneeType: 'GROUP', assigneeRef: { groupId } });
  const selectRole  = (roleName) => onChange?.({ assigneeType: 'ROLE',  assigneeRef: { roleName } });

  const selectedKey = (() => {
    if (!value || !value.assigneeType) return null;
    if (value.assigneeType === 'USER')  return value.assigneeRef?.userId  || null;
    if (value.assigneeType === 'GROUP') return value.assigneeRef?.groupId || null;
    if (value.assigneeType === 'ROLE')  return value.assigneeRef?.roleName || null;
    // RC-221 — DYNAMIC + EXTERNAL_SUBJECT don't have a single picked-key
    // like the other three; their "selected" state is the entire small
    // config blob. The bottom Selected: chip renders a summary string
    // (built in labelForSelection) when the minimum required field is
    // set so the operator still gets visual confirmation of their entry.
    if (value.assigneeType === 'DYNAMIC') return value.assigneeRef?.sourceExpression || null;
    if (value.assigneeType === 'EXTERNAL_SUBJECT') return value.assigneeRef?.stepId || null;
    return null;
  })();

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div>
      <Label required={required}>{label}</Label>

      {/* Tab segmented control — Button group with primary/secondary variants.
          RC-221: 5 tabs total. The two new modes (DYNAMIC, External Subject)
          render config sub-forms instead of pickable lists — see the
          per-tab branches below. `flex-wrap` allows the strip to wrap on
          narrow Designer slideout widths without overflowing. */}
      <div role="group" aria-label="Assignee type" className="inline-flex flex-wrap gap-1 mb-2">
        <TypeButton
          active={assigneeType === 'USER'}
          icon={<User />}
          label="User"
          onClick={() => handleTypeChange('USER')}
          disabled={readOnly}
        />
        <TypeButton
          active={assigneeType === 'GROUP'}
          icon={<Users />}
          label="Group"
          onClick={() => handleTypeChange('GROUP')}
          disabled={readOnly}
        />
        <TypeButton
          active={assigneeType === 'ROLE'}
          icon={<Shield />}
          label="Role"
          onClick={() => handleTypeChange('ROLE')}
          disabled={readOnly}
        />
        <TypeButton
          active={assigneeType === 'DYNAMIC'}
          icon={<Variable />}
          label="Dynamic"
          onClick={() => handleTypeChange('DYNAMIC')}
          disabled={readOnly}
        />
        <TypeButton
          active={assigneeType === 'EXTERNAL_SUBJECT'}
          icon={<Globe />}
          label="External"
          onClick={() => handleTypeChange('EXTERNAL_SUBJECT')}
          disabled={readOnly}
        />
      </div>

      {/* Search box — typeahead client-side filter.
          RC-213 — hidden on ROLE tab (canonical 12-role catalog, renders
          as a Select instead of search+list).
          RC-221 — also hidden on DYNAMIC + EXTERNAL_SUBJECT (those render
          config sub-forms, not pickable candidate lists). */}
      {!readOnly && assigneeType !== 'ROLE'
                 && assigneeType !== 'DYNAMIC'
                 && assigneeType !== 'EXTERNAL_SUBJECT' && (
        <div className="mb-2">
          <Input
            size="sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder(assigneeType)}
            aria-label="Search candidates"
          />
        </div>
      )}

      {/* Per-type surface — candidate list for USER/GROUP/ROLE, config
          sub-form for DYNAMIC/EXTERNAL_SUBJECT. The container styles are
          identical so all five tabs read as the same picker affordance. */}
      <div className="border border-v2-border-subtle rounded-v2-md bg-v2-surface max-h-64 overflow-y-auto">
        {loading && (
          <div className="text-[12px] text-v2-ink-muted px-3 py-4 text-center">
            Loading {assigneeType.toLowerCase()}s…
          </div>
        )}
        {!loading && fetchError && (
          <div className="text-[12px] text-v2-brand px-3 py-3 flex items-center gap-2">
            <AlertCircle size={14} aria-hidden="true" />
            {fetchError}
          </div>
        )}
        {!loading && !fetchError && assigneeType === 'USER' && (
          <CandidateList
            items={filteredUsers}
            getKey={(u) => u.id}
            getLabel={(u) => u.name || u.email || u.id}
            getSubtitle={(u) => u.email}
            selectedKey={selectedKey}
            onSelect={(u) => selectUser(u.id)}
            emptyMessage="No users in this tenant."
            readOnly={readOnly}
            icon={<User />}
          />
        )}
        {!loading && !fetchError && assigneeType === 'GROUP' && (
          <CandidateList
            items={filteredGroups}
            getKey={(g) => g.id}
            getLabel={(g) => g.name}
            getSubtitle={(g) => g.description}
            selectedKey={selectedKey}
            onSelect={(g) => selectGroup(g.id)}
            emptyMessage="No groups in this tenant — create one in Team → Groups first."
            readOnly={readOnly}
            icon={<Users />}
          />
        )}
        {!loading && !fetchError && assigneeType === 'ROLE' && (
          <RoleSelect
            roles={roles}
            selectedKey={selectedKey}
            onSelect={selectRole}
            readOnly={readOnly}
          />
        )}
        {assigneeType === 'DYNAMIC' && (
          <DynamicConfig
            assigneeRef={value?.assigneeRef}
            onChange={patchDynamicRef}
            readOnly={readOnly}
          />
        )}
        {assigneeType === 'EXTERNAL_SUBJECT' && (
          <ExternalSubjectConfig
            assigneeRef={value?.assigneeRef}
            onChange={patchExternalRef}
            readOnly={readOnly}
          />
        )}
      </div>

      {/* Inline current-selection chip — confirmation cue */}
      {selectedKey && (
        <div className="mt-2 flex items-center gap-1.5 text-[11.5px] text-v2-ink-secondary">
          <span>Selected:</span>
          <Pill tone="action" variant="soft">
            {labelForSelection(assigneeType, selectedKey, users, groups, roles)}
          </Pill>
        </div>
      )}

      {(hint || error) && (
        <div className={`mt-1 text-[11.5px] ${error ? 'text-v2-brand' : 'text-v2-ink-muted'}`}>
          {error || hint}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────

function TypeButton({ active, icon, label, onClick, disabled }) {
  return (
    <Button
      variant={active ? 'primary' : 'secondary'}
      size="sm"
      iconLeft={icon}
      onClick={onClick}
      disabled={disabled}
      ariaLabel={`Assign to a ${label.toLowerCase()}`}
    >
      {label}
    </Button>
  );
}

function CandidateList({ items, getKey, getLabel, getSubtitle, selectedKey, onSelect, emptyMessage, readOnly, icon }) {
  if (!items || items.length === 0) {
    return (
      <div className="text-[12px] text-v2-ink-muted px-3 py-4 text-center italic">
        {emptyMessage}
      </div>
    );
  }
  return (
    <ul className="list-none p-0 m-0">
      {items.map((it) => {
        const key = getKey(it);
        const selected = String(selectedKey) === String(key);
        return (
          <li key={key}>
            <button
              type="button"
              onClick={() => !readOnly && onSelect(it)}
              disabled={readOnly}
              className={
                'v2-focus-ring w-full text-left px-3 py-2 flex items-center gap-2 ' +
                'border-b border-v2-border-subtle last:border-b-0 ' +
                'text-[12.5px] ' +
                (selected
                  ? 'bg-v2-action-soft text-v2-action font-semibold'
                  : 'bg-v2-surface text-v2-ink hover:bg-v2-muted') + ' ' +
                'disabled:cursor-not-allowed disabled:opacity-60'
              }
            >
              <span aria-hidden="true" className="text-v2-ink-muted">{icon}</span>
              <span className="flex-1 min-w-0">
                <span className="block truncate">{getLabel(it)}</span>
                {getSubtitle(it) && (
                  <span className="block text-[11px] text-v2-ink-muted truncate">{getSubtitle(it)}</span>
                )}
              </span>
              {selected && <Pill tone="action" variant="soft">selected</Pill>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function searchPlaceholder(type) {
  if (type === 'USER')  return 'Search users by name or email…';
  if (type === 'GROUP') return 'Search groups by name…';
  if (type === 'ROLE')  return 'Search roles…';
  return 'Search…';
}

/**
 * RC-213 (2026-06-23) — Role tab rendered as a V2 Select dropdown
 * instead of the search + candidate-list pattern USER / GROUP use.
 *
 * <p><b>Why a dropdown for ROLE specifically.</b> Roles are the
 * canonical 12-role catalog from §3 of RBAC_REDESIGN_V2.md
 * (Platform Admin → Viewer); the list is short, fixed, and
 * platform-wide. A search-and-pick affordance is overkill — operators
 * benefit more from seeing every option at once with level + perm-count
 * context. USER / GROUP keep the candidate-list pattern because their
 * lists can be hundreds of rows.</p>
 *
 * <p><b>Sort order.</b> Most-powerful first (level desc), so the
 * familiar roles appear at the top of the menu. Null-level (custom /
 * platform-internal) sorts last.</p>
 *
 * <p><b>Empty state.</b> If the BE returns an empty role list
 * (catalog endpoint truly empty, or the unwrap regression that
 * RC-213 fixed re-appears), render a clear in-context message that
 * points at the actual cause rather than a misleading "No roles".</p>
 */
function RoleSelect({ roles, selectedKey, onSelect, readOnly }) {
  if (!roles || roles.length === 0) {
    return (
      <div className="text-[12px] text-v2-ink-muted px-3 py-4 text-center italic">
        No roles returned by /api/v1/rbac/roles. Contact a Platform Admin
        if the catalog should be populated.
      </div>
    );
  }
  // Most-powerful first (level desc). Null level sorts to the end.
  const sorted = [...roles].sort((a, b) => {
    const la = a?.level == null ? -1 : a.level;
    const lb = b?.level == null ? -1 : b.level;
    return lb - la;
  });
  return (
    <div className="px-3 py-3">
      <Select
        size="sm"
        value={selectedKey || ''}
        onChange={(e) => {
          const v = e.target.value;
          if (v) onSelect(v);
        }}
        disabled={readOnly}
        aria-label="Select role"
      >
        <option value="">— Select a role —</option>
        {sorted.map((r) => {
          const name = r?.name || r?.role;
          if (!name) return null;
          const levelPart = r.level != null ? ` · level ${r.level}` : '';
          const permsPart = Array.isArray(r.permissions)
            ? ` · ${r.permissions.length} permission${r.permissions.length === 1 ? '' : 's'}`
            : '';
          return (
            <option key={name} value={name}>
              {name}{levelPart}{permsPart}
            </option>
          );
        })}
      </Select>
    </div>
  );
}

function describeRole(r) {
  // RbacService returns role descriptors with optional permissions/scope info.
  // Surface what's available; fall back to no-subtitle if nothing useful.
  if (r?.description) return r.description;
  if (typeof r?.permissions === 'number') return `${r.permissions} permission${r.permissions === 1 ? '' : 's'}`;
  if (Array.isArray(r?.permissions)) return `${r.permissions.length} permissions`;
  return null;
}

function labelForSelection(type, key, users, groups, roles) {
  if (type === 'USER') {
    const u = (users || []).find((x) => String(x.id) === String(key));
    return u ? (u.name || u.email || u.id) : key;
  }
  if (type === 'GROUP') {
    const g = (groups || []).find((x) => String(x.id) === String(key));
    return g ? g.name : key;
  }
  if (type === 'ROLE') {
    // Role identifier IS the name, so just echo
    return key;
  }
  if (type === 'DYNAMIC') {
    // key is sourceExpression; show the expression literally so the
    // operator can confirm they typed the right variable name.
    return `expression: ${key}`;
  }
  if (type === 'EXTERNAL_SUBJECT') {
    // key is stepId; prefix with "step:" so the user reads it as a
    // route fragment, not as a free-form label.
    return `step: ${key}`;
  }
  return key;
}

// ────────────────────────────────────────────────────────────────────────
// RC-221 — defaults + new sub-form components
// ────────────────────────────────────────────────────────────────────────

/**
 * Default assigneeRef shape per type. Returning a fully-formed (if blank)
 * shape — instead of bare {} — lets the BE schema validator distinguish
 * "the operator hasn't filled out DYNAMIC yet" (sourceExpression='')
 * from "the operator is on the USER tab and hasn't picked" (userId='').
 * The Form Designer's existing readAssignment merge will leave these
 * defaults intact when the user opens an existing assignment that was
 * authored before RC-221.
 */
function defaultRefFor(type) {
  switch (type) {
    case 'USER':  return {};
    case 'GROUP': return {};
    case 'ROLE':  return {};
    case 'DYNAMIC': return {
      resolveVia: 'EMAIL',
      sourceExpression: '',
      externalFallback: true,
    };
    case 'EXTERNAL_SUBJECT': return {
      stepId: '',
      notifyVia: 'WEBHOOK',
    };
    default: return {};
  }
}

/**
 * DYNAMIC sub-form — the assignee identity is resolved at runtime from a
 * workflow variable expression (e.g. a customer's SPA POSTed
 * `requester_email` as part of /start; a downstream form_task consumes
 * it as `${claim_intake.output.requester_email}`).
 *
 * <p>Shape rules are enforced by the shared validator
 * (formTaskAssignmentValidator.js). The picker only renders the inputs —
 * validation runs on Save via the canvas Issues panel. Each field has
 * inline helper text so the operator understands the runtime behavior
 * without consulting the design doc.</p>
 */
function DynamicConfig({ assigneeRef, onChange, readOnly }) {
  const r = assigneeRef || {};
  // Sort the enum values so the dropdown order is deterministic. Values
  // come from LEGAL_DYNAMIC_RESOLVE_VIA so adding a new resolution mode
  // to the validator's Set automatically surfaces it here.
  const resolveViaOptions = Array.from(LEGAL_DYNAMIC_RESOLVE_VIA).sort();
  return (
    <div className="px-3 py-3 space-y-3">
      <FieldGroup
        label="Resolve identity via"
        hint="How the expression value maps to a user. EMAIL looks up a Prove7 user by email; USER_ID expects a UUID; EXTERNAL_SUBJECT_REF treats the value as an opaque customer-side subject id."
      >
        <Select
          size="sm"
          value={r.resolveVia || 'EMAIL'}
          onChange={(e) => onChange({ resolveVia: e.target.value })}
          disabled={readOnly}
          aria-label="Resolve via"
        >
          {resolveViaOptions.map((v) => (
            <option key={v} value={v}>{humanResolveVia(v)}</option>
          ))}
        </Select>
      </FieldGroup>

      <FieldGroup
        label="Source expression"
        hint="A workflow-variable reference. Example: ${claim_intake.output.requester_email}. Literals (without ${}) are rejected on save — use a STATIC mode for those."
      >
        <Input
          size="sm"
          value={r.sourceExpression || ''}
          onChange={(e) => onChange({ sourceExpression: e.target.value })}
          placeholder="${claim_intake.output.requester_email}"
          disabled={readOnly}
          aria-label="Source expression"
        />
      </FieldGroup>

      {/* externalFallback only meaningful for EMAIL resolution — for
          USER_ID and EXTERNAL_SUBJECT_REF there's no "no Prove7 user
          found" branch (the customer-side identifier IS the address).
          Hiding when irrelevant prevents operator confusion. */}
      {(r.resolveVia || 'EMAIL') === 'EMAIL' && (
        <FieldGroup
          label="If no Prove7 user matches the email"
          hint="EMAIL resolution can fall back to sending the form via a signed magic-link to that email address (no Prove7 login required). Runtime lands in RC-222."
        >
          <Select
            size="sm"
            value={r.externalFallback === false ? 'false' : 'true'}
            onChange={(e) => onChange({ externalFallback: e.target.value === 'true' })}
            disabled={readOnly}
            aria-label="External fallback"
          >
            <option value="true">Send a signed magic-link to that email</option>
            <option value="false">Fail the task (no fallback)</option>
          </Select>
        </FieldGroup>
      )}
    </div>
  );
}

/**
 * EXTERNAL_SUBJECT sub-form — Shape A round-trip per FORM_INGESTION_MODEL.md
 * §3. The form is filled IN the customer's UI, not in a Prove7 inbox.
 * The workflow pauses for a webhook callback to
 * /api/v1/workflows/runs/{run_id}/steps/{stepId}/submit. There is NO
 * internal assignee for this mode — the lifecycle workflow branches on
 * type BEFORE invoking AssigneeResolverService (per RC-219 contract).
 */
function ExternalSubjectConfig({ assigneeRef, onChange, readOnly }) {
  const r = assigneeRef || {};
  const notifyViaOptions = Array.from(LEGAL_EXTERNAL_NOTIFY_VIA).sort();
  return (
    <div className="px-3 py-3 space-y-3">
      <FieldGroup
        label="Step identifier"
        hint="URL-safe fragment for the customer's POST-back endpoint /api/v1/workflows/runs/{run_id}/steps/{stepId}/submit. No slashes, no spaces. Use snake_case or kebab-case."
      >
        <Input
          size="sm"
          value={r.stepId || ''}
          onChange={(e) => onChange({ stepId: e.target.value })}
          placeholder="upload_evidence"
          disabled={readOnly}
          aria-label="Step ID"
        />
      </FieldGroup>

      <FieldGroup
        label="Notify the customer's UI via"
        hint="How Prove7 signals the customer's UI that this step is ready for input. WEBHOOK posts to their registered callback URL; SSE pushes over the streaming API; NONE assumes their UI polls /api/v1/workflows/runs/{run_id}/state."
      >
        <Select
          size="sm"
          value={r.notifyVia || 'WEBHOOK'}
          onChange={(e) => onChange({ notifyVia: e.target.value })}
          disabled={readOnly}
          aria-label="Notify via"
        >
          {notifyViaOptions.map((v) => (
            <option key={v} value={v}>{humanNotifyVia(v)}</option>
          ))}
        </Select>
      </FieldGroup>
    </div>
  );
}

function humanResolveVia(v) {
  if (v === 'EMAIL') return 'Email address (look up Prove7 user by email)';
  if (v === 'USER_ID') return 'Prove7 user UUID';
  if (v === 'EXTERNAL_SUBJECT_REF') return 'Opaque customer-side subject id';
  return v;
}

function humanNotifyVia(v) {
  if (v === 'WEBHOOK') return 'Webhook (POST to customer callback URL)';
  if (v === 'SSE') return 'Server-Sent Events (Prove7 streaming API)';
  if (v === 'NONE') return 'None — customer polls run state';
  return v;
}

export default AssigneePicker;
